import { useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Users,
  UserCheck,
  UserX,
  UserMinus,
  Filter,
  RefreshCw,
  AlertCircle,
  BookOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileFooter,
  TileHeader,
  TileTitle
} from '../components/ui';

/** Link wie Showcase-`Button` outline sm (kein verschachteltes `<button>`). */
const linkOutlineSmClass =
  'btn btn-outline btn-sm inline-flex items-center justify-center gap-2 font-medium no-underline';

interface Student {
  sophomorixUnid: string;
  Vorname: string;
  Name: string;
  Klasse: string;
  Lehrer?: string;
  SessionID?: number;
}

interface AttendanceData {
  inLernbuero: Student[];
  notAssigned: Student[];
  sick: Student[];
}

const Attendance: React.FC = () => {
  const queryClient = useQueryClient();

  // Filter states
  const [filterKlasse, setFilterKlasse] = useState<string>('*');
  const [filterName, setFilterName] = useState<string>('');

  // Debounced filter states for API calls
  const [debouncedFilterKlasse, setDebouncedFilterKlasse] = useState<string>('*');
  const [debouncedFilterName, setDebouncedFilterName] = useState<string>('');
  const [debouncedFilterActive, setDebouncedFilterActive] = useState<boolean>(false);

  // Selection states
  const [selectedInLernbuero, setSelectedInLernbuero] = useState<string[]>([]);
  const [selectedNotAssigned, setSelectedNotAssigned] = useState<string[]>([]);
  const [selectedSick, setSelectedSick] = useState<string[]>([]);

  // Current session from sessionStorage
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

  // Load current session from sessionStorage
  useEffect(() => {
    const savedSessionId = sessionStorage.getItem('lernbuero-selected-session-id');
    if (savedSessionId) {
      setCurrentSessionId(parseInt(savedSessionId));
    }
  }, []);

  // Debouncing effect for filter name
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterName(filterName);
      // Auto-activate filter when user types
      if (filterName.trim() !== '') {
        setDebouncedFilterActive(true);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [filterName]);

  // Debouncing effect for filter klasse
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterKlasse(filterKlasse);
      // Auto-activate filter when user selects a specific class
      if (filterKlasse !== '*') {
        setDebouncedFilterActive(true);
      }
    }, 300); // 300ms delay for dropdown

    return () => clearTimeout(timer);
  }, [filterKlasse]);

  // Fetch classes for filter
  const { data: classesResponse } = useQuery(
    ['attendance-classes'],
    () => api.get('/students/classes').then(res => res.data)
  );
  const classes = classesResponse?.data || [];

  // Fetch attendance data
  const { data: attendanceData, isLoading, refetch } = useQuery<AttendanceData>(
    ['attendance-data', debouncedFilterKlasse, debouncedFilterName, debouncedFilterActive],
    () => api.get('/attendance/lists', {
      params: {
        klasse: debouncedFilterKlasse !== '*' ? debouncedFilterKlasse : undefined,
        name: debouncedFilterName || undefined,
        filterActive: debouncedFilterActive
      }
    }).then(res => res.data.data),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Move student to Lernbüro mutation
  const moveToLernbueroMutation = useMutation(
    (studentIds: string[]) => api.post('/attendance/move-to-lernbuero', {
      studentIds,
      sessionId: currentSessionId
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attendance-data']);
        toast.success('Schüler ins Lernbüro übernommen');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler beim Übernehmen');
      }
    }
  );

  // Move student to sick mutation
  const moveToSickMutation = useMutation(
    (studentIds: string[]) => api.post('/attendance/move-to-sick', { studentIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attendance-data']);
        toast.success('Krankmeldung erstellt');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler bei Krankmeldung');
      }
    }
  );

  // Remove from Lernbüro mutation
  const removeFromLernbueroMutation = useMutation(
    (studentIds: string[]) => api.post('/attendance/remove-from-lernbuero', { studentIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attendance-data']);
        toast.success('Schüler aus Lernbüro entfernt');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler beim Entfernen');
      }
    }
  );

  // Remove sick status mutation
  const removeSickStatusMutation = useMutation(
    (studentIds: string[]) => api.post('/attendance/remove-sick-status', { studentIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attendance-data']);
        toast.success('Krankmeldung entfernt');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler beim Entfernen');
      }
    }
  );

  // Handle filter changes
  const handleFilterChange = () => {
    setDebouncedFilterActive(true);
  };

  const handleResetFilter = () => {
    setFilterKlasse('*');
    setFilterName('');
    setDebouncedFilterKlasse('*');
    setDebouncedFilterName('');
    setDebouncedFilterActive(false);
  };

  // Handle student selection
  const toggleSelection = (studentId: string, list: 'inLernbuero' | 'notAssigned' | 'sick') => {
    switch (list) {
      case 'inLernbuero':
        setSelectedInLernbuero(prev =>
          prev.includes(studentId)
            ? prev.filter(id => id !== studentId)
            : [...prev, studentId]
        );
        break;
      case 'notAssigned':
        setSelectedNotAssigned(prev =>
          prev.includes(studentId)
            ? prev.filter(id => id !== studentId)
            : [...prev, studentId]
        );
        break;
      case 'sick':
        setSelectedSick(prev =>
          prev.includes(studentId)
            ? prev.filter(id => id !== studentId)
            : [...prev, studentId]
        );
        break;
    }
  };

  // Handle move actions
  const handleMoveToLernbuero = () => {
    if (!currentSessionId) {
      toast.error('Bitte wählen Sie zuerst eine Sitzung im Lernbüro aus');
      return;
    }
    if (selectedNotAssigned.length === 0) {
      toast.error('Bitte wählen Sie Schüler aus');
      return;
    }
    moveToLernbueroMutation.mutate(selectedNotAssigned);
    setSelectedNotAssigned([]);
  };

  const handleMoveToSick = () => {
    if (selectedNotAssigned.length === 0) {
      toast.error('Bitte wählen Sie Schüler aus');
      return;
    }
    moveToSickMutation.mutate(selectedNotAssigned);
    setSelectedNotAssigned([]);
  };

  const handleRemoveFromLernbuero = () => {
    if (selectedInLernbuero.length === 0) {
      toast.error('Bitte wählen Sie Schüler aus');
      return;
    }
    removeFromLernbueroMutation.mutate(selectedInLernbuero);
    setSelectedInLernbuero([]);
  };

  const handleRemoveSickStatus = () => {
    if (selectedSick.length === 0) {
      toast.error('Bitte wählen Sie Schüler aus');
      return;
    }
    removeSickStatusMutation.mutate(selectedSick);
    setSelectedSick([]);
  };

  // Render student table
  const renderStudentTable = (
    students: Student[],
    listType: 'inLernbuero' | 'notAssigned' | 'sick',
    title: string,
    icon: ReactNode,
    iconWrapClass: string
  ) => {
    const selectedList = listType === 'inLernbuero' ? selectedInLernbuero :
      listType === 'notAssigned' ? selectedNotAssigned : selectedSick;

    const colCount = listType === 'inLernbuero' ? 5 : 3;

    // Render buttons based on table type
    const renderButtons = () => {
      switch (listType) {
        case 'inLernbuero':
          return (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={handleRemoveFromLernbuero}
              disabled={selectedInLernbuero.length === 0 || removeFromLernbueroMutation.isLoading}
            >
              Aus Lernbüro entfernen
            </Button>
          );
        case 'notAssigned':
          return (
            <div className="flex w-full flex-col gap-2">
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={handleMoveToLernbuero}
                disabled={!currentSessionId || selectedNotAssigned.length === 0 || moveToLernbueroMutation.isLoading}
              >
                Ins Lernbüro
              </Button>
              <Button
                type="button"
                variant="danger"
                className="w-full"
                onClick={handleMoveToSick}
                disabled={selectedNotAssigned.length === 0 || moveToSickMutation.isLoading}
              >
                <UserMinus className="h-4 w-4" />
                Krankmelden
              </Button>
            </div>
          );
        case 'sick':
          return (
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={handleRemoveSickStatus}
              disabled={selectedSick.length === 0 || removeSickStatusMutation.isLoading}
            >
              <UserCheck className="h-4 w-4" />
              Krankmeldung entfernen
            </Button>
          );
        default:
          return null;
      }
    };

    return (
      <Tile className="flex h-full min-h-0 flex-col">
        <TileHeader className="flex-shrink-0 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconWrapClass}`}
              aria-hidden
            >
              {icon}
            </div>
            <TileTitle as="h3" className="text-base">
              {title}
            </TileTitle>
            <Badge variant="neutral" className="tabular-nums">
              {students.length}
            </Badge>
          </div>
        </TileHeader>

        <TileContent className="min-h-0 flex-1 overflow-hidden px-0 py-0">
          <div className="h-full min-h-0 overflow-y-auto px-4 py-2">
            <TableScrollArea className="border-0 bg-transparent shadow-none dark:border-0 dark:bg-transparent dark:shadow-none">
              <Table className="min-w-full divide-y-0">
                <TableHead>
                  <TableRow className="border-0 hover:bg-transparent dark:hover:bg-transparent">
                    <TableHeaderCell className="normal-case">Klasse</TableHeaderCell>
                    <TableHeaderCell className="normal-case">Vorname</TableHeaderCell>
                    <TableHeaderCell className="normal-case">Name</TableHeaderCell>
                    {listType === 'inLernbuero' && (
                      <>
                        <TableHeaderCell className="normal-case">Lehrer</TableHeaderCell>
                        <TableHeaderCell className="normal-case">Session</TableHeaderCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody className="divide-y-0">
                  {students.length === 0 ? (
                    <EmptyTableRow
                      colSpan={colCount}
                      title="Keine Einträge"
                      description="Für diese Liste liegen aktuell keine Schüler vor."
                    />
                  ) : (
                    students.map((student, index) => (
                      <TableRow
                        key={`${listType}-${student.sophomorixUnid || 'unknown'}-${index}`}
                        className={`cursor-pointer border-b border-gray-100 dark:border-gray-800 ${selectedList.includes(student.sophomorixUnid)
                          ? '!bg-primary-50 dark:!bg-primary-950/45'
                          : ''
                          }`}
                        onClick={() => toggleSelection(student.sophomorixUnid, listType)}
                      >
                        <TableCell className="py-2">{student.Klasse}</TableCell>
                        <TableCell className="py-2">{student.Vorname}</TableCell>
                        <TableCell className="py-2">{student.Name}</TableCell>
                        {listType === 'inLernbuero' && (
                          <>
                            <TableCell className="py-2">{student.Lehrer || '—'}</TableCell>
                            <TableCell className="py-2 tabular-nums">{student.SessionID ?? '—'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableScrollArea>
          </div>
        </TileContent>

        <TileFooter className="flex-shrink-0">
          {renderButtons()}
        </TileFooter>
      </Tile>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-600 dark:text-gray-400">
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
        <span className="ml-2 text-sm">Lade Anwesenheitsdaten…</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6 p-6">
      <ListToolbar
        title="Anwesenheit"
        description="Lernbüro- und Kranklisten verwalten. Schriftgröße und Farbschema wie unter Darstellung in /Showcase."
        trailing={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void refetch()}
              title="Daten neu laden"
            >
              <RefreshCw className="h-4 w-4" />
              Aktualisieren
            </Button>
            <Link to="/lernbuero" className={linkOutlineSmClass}>
              Lernbüro
            </Link>
            <Link to="/klassenbuch" className={linkOutlineSmClass}>
              <BookOpen className="h-4 w-4" aria-hidden />
              Klassenbuch
            </Link>
          </div>
        }
      />

      {/* Filter Section */}
      <Tile>
        <TileHeader>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600 dark:text-gray-400" aria-hidden />
            <TileTitle as="h2" className="text-base">
              Filteroptionen
            </TileTitle>
            {debouncedFilterActive ? (
              <Badge variant="info" className="ml-1">
                aktiv
              </Badge>
            ) : null}
          </div>
        </TileHeader>
        <TileContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Klasse
              </label>
              <select
                value={filterKlasse}
                onChange={(e) => setFilterKlasse(e.target.value)}
                className="input-field w-full"
              >
                <option value="*">Alle Klassen</option>
                {classes.map((klasse: { Klasse: string }) => (
                  <option key={klasse.Klasse} value={klasse.Klasse}>
                    {klasse.Klasse}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                type="text"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Name suchen…"
                className="input-field w-full"
              />
            </div>

            <div className="flex items-end">
              <Button type="button" variant="primary" className="w-full" onClick={handleFilterChange}>
                Filter aktivieren
              </Button>
            </div>

            <div className="flex items-end">
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={handleResetFilter}
              >
                <RefreshCw className="h-4 w-4" />
                Zurücksetzen
              </Button>
            </div>
          </div>
        </TileContent>
      </Tile>

      {/* Three Tables */}
      <div className="grid min-h-[400px] max-h-[calc(100vh-200px)] grid-cols-1 gap-4 lg:h-[calc(100vh-400px)] lg:grid-cols-3">
        {renderStudentTable(
          attendanceData?.inLernbuero || [],
          'inLernbuero',
          'Schüler im Lernbüro',
          <UserCheck className="h-5 w-5 text-green-700 dark:text-green-400" />,
          'bg-green-100 dark:bg-green-950/50'
        )}

        {renderStudentTable(
          attendanceData?.notAssigned || [],
          'notAssigned',
          'Schüler ohne Lernbüro',
          <Users className="h-5 w-5 text-gray-700 dark:text-gray-300" />,
          'bg-gray-100 dark:bg-gray-800'
        )}

        {renderStudentTable(
          attendanceData?.sick || [],
          'sick',
          'Schüler entschuldigt/krank',
          <UserX className="h-5 w-5 text-red-700 dark:text-red-400" />,
          'bg-red-100 dark:bg-red-950/45'
        )}
      </div>

      {/* Session Warning */}
      {!currentSessionId && (
        <div className="flex justify-center">
          <div
            className="flex max-w-2xl items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <span>
              Bitte wählen Sie zuerst eine Sitzung im Lernbüro aus, um Schüler ins Lernbüro zu übernehmen.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
